import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { GavelOutlined } from "@mui/icons-material";
import type { PermitSummary } from "../../../Services/permitSummaryApi";
import { DASH } from "./tokens";

// LADBS type codes, in words a homeowner uses.
const TYPE_LABELS: Record<string, string> = {
  "Bldg-Alter/Repair": "alterations & repairs",
  "Bldg-Addition": "additions",
  "Bldg-New": "new construction",
  "Bldg-Demolition": "demolition",
  "Swimming-Pool/Spa": "pool/spa",
  "Fire Sprinkler": "fire sprinklers",
  // Electrical and mechanical permits (October 2026).
  Electrical: "electrical",
  Plumbing: "plumbing",
  HVAC: "heating & cooling",
  Elevator: "elevator",
};
const typeLabel = (t: string) => TYPE_LABELS[t] || t.toLowerCase();

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "3 permits on public record", "2011–2024 · 1 never finaled · alterations & repairs, pool/spa" */
export const describePermits = (s: PermitSummary) => {
  const years =
    s.firstYear && s.lastYear ? (s.firstYear === s.lastYear ? String(s.firstYear) : `${s.firstYear}–${s.lastYear}`) : null;
  const details = [
    years,
    s.notFinaledCount ? `${s.notFinaledCount} never finaled` : null,
    s.topTypes.length ? s.topTypes.map(typeLabel).join(", ") : null,
  ].filter(Boolean);
  return { headline: `${plural(s.count, "permit")} on public record`, detail: details.join(" · ") };
};

type Props = {
  summary: PermitSummary;
  /** pending: their claim is in review, so they'll see them once it's approved. */
  pending?: boolean;
  /** glass: inside the photo header. card: on the page. */
  variant?: "glass" | "card";
};

/**
 * The hook for an unclaimed LA home (October 2026, Asher): show that public
 * permits exist before the claim, so someone opening the link has a reason to
 * claim it. Only a summary; the permits themselves open up after the claim.
 */
const PermitTeaser: React.FC<Props> = ({ summary, pending, variant = "card" }) => {
  const { headline, detail } = describePermits(summary);
  const glass = variant === "glass";
  const after = pending
    ? "You'll see each one as soon as your claim is approved."
    : "Claim this home to see each one and add them to its history.";
  return (
    <Box
      sx={{
        borderRadius: "12px",
        p: glass ? 2.5 : 4,
        ...(glass
          ? { bgcolor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.3)" }
          : { bgcolor: DASH.amberTint, border: "1px solid rgba(251,190,104,0.6)" }),
      }}
      data-testid="permit-teaser"
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <GavelOutlined sx={{ fontSize: glass ? 18 : 22, mt: "2px", color: glass ? "#fff" : DASH.amber }} aria-hidden />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: glass ? 14 : 15.5, color: glass ? "#fff" : "#1d2b2b" }}>
            {headline}
          </Typography>
          {detail && (
            <Typography sx={{ fontSize: glass ? 12.5 : 13.5, mt: 0.5, color: glass ? "rgba(255,255,255,0.85)" : "#5b3a00" }}>
              {detail}
            </Typography>
          )}
          {!glass && (
            <Typography sx={{ fontSize: 13.5, mt: 1, color: DASH.muted }}>
              {after} From the LA Department of Building and Safety.
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default PermitTeaser;
